import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAppContext } from "@/providers";
import { useToast } from "@/components/ui/toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SiteHeader } from "@/components/layout/site-header";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { t } from "@/lib/i18n/messages";
import { resetPassword } from "@/lib/api/auth";
import { useDocumentTitle } from "@/hooks/use-document-title";

export default function ResetPasswordPage() {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const otp = searchParams.get("otp") ?? "";

  useDocumentTitle(`${t(locale, "resetPasswordTitle")} | NCircle`);

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!password) errors["password"] = t(locale, "requiredField");
    else if (password.length < 8) errors["password"] = t(locale, "passwordMinLength");
    if (password !== passwordConfirmation) errors["password_confirmation"] = t(locale, "passwordMismatch");
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const res = await resetPassword({ email, otp, password, password_confirmation: passwordConfirmation });
      if (res.success) {
        toast(t(locale, "passwordResetSuccess"), "success");
        void navigate("/login");
      } else {
        const serverErrors: Record<string, string> = {};
        if (res.errors) {
          Object.entries(res.errors).forEach(([key, msgs]) => {
            serverErrors[key] = msgs[0] ?? "";
          });
        } else {
          serverErrors["password"] = res.message ?? t(locale, "error");
        }
        setFieldErrors(serverErrors);
      }
    } catch {
      setFieldErrors({ password: t(locale, "error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SiteHeader />
      <main
        dir={isRTL ? "rtl" : "ltr"}
        className="flex items-center justify-center py-12 px-4 min-h-[calc(100vh-64px)] pt-24 bg-background"
      >
        <Card className="w-full max-w-[480px] p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-secondary to-brand-green" />
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {t(locale, "resetPasswordTitle")}
            </h1>
            <p className="text-sm text-foreground-secondary">
              {t(locale, "resetPasswordDesc")}
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-6">
            <Input
              id="password"
              type="password"
              dir="ltr"
              label={`${t(locale, "newPasswordLabel")} *`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors["password"]}
              placeholder="••••••••"
            />
            <Input
              id="password_confirmation"
              type="password"
              dir="ltr"
              label={`${t(locale, "confirmPasswordLabel")} *`}
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              error={fieldErrors["password_confirmation"]}
              placeholder="••••••••"
            />
            <Button type="submit" className="w-full h-12 text-base" isLoading={submitting}>
              {t(locale, "resetPasswordBtn")}
            </Button>
          </form>
        </Card>
      </main>
      <ThemeToggle />
    </>
  );
}
